import { useRef, useEffect } from "react";
import {
  getImageDimensionsByUrl,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import debounce from "lodash/debounce";
import CanvasLoader from "../CanvasLoader";
import useEditorContext from "./useEditorContext";

interface ComponentProps {
  url: string;
}

const EditorCanvas = ({ url }: ComponentProps) => {
  const containerElement = useRef<HTMLDivElement>(null);
  const canvasElement = useRef<HTMLCanvasElement>(null);
  const { instance, setInstance, closeModal } = useEditorContext();
  const { toast } = useToast();
  const { t } = useTranslations();

  useEffect(() => {
    const handleResize = debounce(() => {
      if (!instance?.canvas || !instance.image?.object) return;

      const width = instance.image.object.getScaledWidth();
      const height = instance.image.object.getScaledHeight();

      instance.setDimensions(width, height);
    }, 150);

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      handleResize.cancel();
    };
  }, [instance]);

  useEffect(() => {
    (async () => {
      if (!canvasElement.current || instance) return;

      const { width: imgWidth, height: imgHeight } =
        await getImageDimensionsByUrl(url);

      try {
        const canvasLoader = new CanvasLoader(
          canvasElement.current,
          url,
          containerElement
        );
        setInstance(canvasLoader);

        await canvasLoader.init();

        const container = containerElement.current;

        if (canvasLoader.canvas && container) {
          const maxWidth = container.clientWidth;
          const maxHeight = container.clientHeight;

          const scale = Math.min(maxWidth / imgWidth, maxHeight / imgHeight, 1);
          const scaledWidth = Math.round(imgWidth * scale);
          const scaledHeight = Math.round(imgHeight * scale);

          canvasLoader.canvas.selection = false;

          canvasLoader.canvas.setDimensions(
            {
              width: imgWidth,
              height: imgHeight,
            },
            { backstoreOnly: true }
          );

          canvasLoader.canvas.setDimensions(
            {
              width: scaledWidth,
              height: scaledHeight,
            },
            { cssOnly: true }
          );
        }
      } catch (error) {
        toast(
          t({
            id: "imageEditor.error.failedToLoadImage",
            defaultMessage: "Failed to load image",
          }),
          {
            variant: "error",
          }
        );

        closeModal();
      }
    })();

    return () => {
      if (instance) {
        instance.canvas?.dispose();
        setInstance(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, instance?.canvas, toast, t]);

  return (
    <div
      ref={containerElement}
      className="flex h-full max-h-[calc(100vh-20rem)] w-full max-w-[80%] items-center justify-center"
      onContextMenu={(e) => e.preventDefault()}
    >
      <canvas ref={canvasElement} />
    </div>
  );
};

export default EditorCanvas;
